import { localDue } from "./timing.ts"

/**
 * The page's mirror of the server's Path, and the dot that follows it. Every
 * entry carries its own absolute due on the server's clock; the page never
 * computes one. The Walker's dot moves only through these entries (ADR 0003):
 * from a report once audio runs, from a timer at the local due behind the
 * splash. Pure functions only.
 */

/** The distribution a Step was sampled from, keyed by Cell index, as it travels on a Path entry. */
export type Odds = Readonly<Record<string, number>>

/**
 * One Path entry as the server sends it. `due` is server monotonic ms. `count`
 * is the Step's place in the Bar, 0 to 15, which the server owns: the page
 * decides from it and never works it out.
 */
export interface PathEntry {
  readonly gen: number
  readonly step: number
  readonly count: number
  readonly cell: number
  readonly due: number
  readonly odds?: Odds
  readonly late?: boolean
  /** The Forms Jev chose for the hit, by name, once it answered in time. The page reads the ones it knows. */
  readonly forms?: ReadonlyArray<string>
}

/** The entries of one generation, keyed by Step. An older generation is never kept beside a newer one. */
export interface Path {
  readonly gen: number | undefined
  readonly entries: ReadonlyMap<number, PathEntry>
}

export const emptyPath: Path = { gen: undefined, entries: new Map() }

/**
 * Apply a `path` message: a newer generation discards everything pending, an
 * older one is ignored, the same one is written by Step with the latest
 * write winning. The same rules the core applies to Cues.
 */
export const applyEntries = (path: Path, incoming: ReadonlyArray<PathEntry>): Path => {
  let gen = path.gen
  let entries: Map<number, PathEntry> | undefined
  for (const entry of incoming) {
    if (gen !== undefined && entry.gen < gen) continue
    if (entry.gen !== gen) {
      gen = entry.gen
      entries = new Map()
    }
    // The first write of a message copies the Map; the rest write into the copy.
    entries ??= new Map(path.entries)
    entries.set(entry.step, entry)
  }
  return entries === undefined ? path : { gen, entries }
}

export const entryAt = (path: Path, gen: number, step: number): PathEntry | undefined =>
  path.gen === gen ? path.entries.get(step) : undefined

/**
 * The last Step the dot arrived on: the core's last report, fired or missed,
 * or behind the splash the last timer that landed.
 */
export interface Reported {
  readonly gen: number
  readonly step: number
}

/**
 * Of these entries, the ones not yet reported. Re-cueing a reported Step would
 * only produce a spurious missed. Reports can be lost when the ring overflows,
 * so everything at or below the last one is taken as reported too.
 */
export const pendingAmong = (
  path: Path,
  reported: Reported | undefined,
  entries: ReadonlyArray<PathEntry>
): ReadonlyArray<PathEntry> => {
  const floor = reported !== undefined && reported.gen === path.gen ? reported.step : -1
  return entries.filter((entry) => entry.gen === path.gen && entry.step > floor)
}

/**
 * A `path` message, applied: the Path it leaves, and the entries in it to cue.
 * That is every entry the core has not yet reported and that is still the last
 * word on its Step, so a Step sent twice in one message is cued once. A whole
 * pending Path re-emitted on a tempo re-base is re-cued entry by entry, each at
 * its new due, by the same rule as a single entry as it is decided.
 */
export const receivePath = (
  path: Path,
  reported: Reported | undefined,
  entries: ReadonlyArray<PathEntry>
): { readonly path: Path; readonly cue: ReadonlyArray<PathEntry> } => {
  const next = applyEntries(path, entries)
  return {
    path: next,
    cue: pendingAmong(next, reported, entries).filter(
      (entry) => entryAt(next, entry.gen, entry.step) === entry
    )
  }
}

/** The entries a new mapping re-cues: everything in the Path the core has not yet reported, in Step order. */
export const pendingEntries = (path: Path, reported: Reported | undefined): ReadonlyArray<PathEntry> =>
  pendingAmong(path, reported, [...path.entries.values()])
    .slice()
    .sort((a, b) => a.step - b.step)

/** Forget entries the core has reported. The report's own entry has been looked up by then. */
export const forgetReported = (path: Path, reported: Reported): Path => {
  if (reported.gen !== path.gen) return path
  const entries = new Map<number, PathEntry>()
  for (const [step, entry] of path.entries) if (step > reported.step) entries.set(step, entry)
  return { gen: path.gen, entries }
}

/** The dot arriving on a Cell at a page time, for one Step. */
export interface Move {
  readonly at: number
  readonly cell: number
  readonly gen: number
  readonly step: number
  /** The core reported the Cue missed: the dot still moves, but nothing sounded. */
  readonly missed?: boolean
  /** The Forms of the hit it lands on, when Jev chose any: the Grid draws them as it lands. */
  readonly forms?: ReadonlyArray<string>
}

/** Behind the splash nothing sounds, so the dot moves at each entry's local due and no later. */
export const timerMoves = (entries: ReadonlyArray<PathEntry>, offset: number): ReadonlyArray<Move> =>
  entries.map((entry) => ({
    at: localDue(entry.due, offset),
    cell: entry.cell,
    gen: entry.gen,
    step: entry.step,
    ...(entry.forms === undefined ? {} : { forms: entry.forms })
  }))

/** Add moves to the pending list, each replacing any earlier move for the same Step. */
export const retime = (moves: ReadonlyArray<Move>, next: ReadonlyArray<Move>): ReadonlyArray<Move> => [
  ...moves.filter((move) => !next.some((n) => n.gen === move.gen && n.step === move.step)),
  ...next
]

/**
 * Take the moves that are due: the dot lands on the latest of them and the
 * rest wait. Landing on the latest, not each in turn, is what keeps a tab that
 * was hidden for a while from replaying the walk when it comes back. The due
 * moves before it are handed back as passed, in the order they were heard, so
 * the trail still has every Step.
 */
export const drainMoves = (
  moves: ReadonlyArray<Move>,
  now: number
): {
  readonly passed: ReadonlyArray<Move>
  readonly landed: Move | undefined
  readonly pending: ReadonlyArray<Move>
} => {
  let landed: Move | undefined
  for (const move of moves) {
    if (move.at <= now && (landed === undefined || move.at >= landed.at)) landed = move
  }
  // Nothing due is the common frame: hand the same list back rather than a copy.
  if (landed === undefined) return { passed: [], landed, pending: moves }
  const passed = moves.filter((move) => move.at <= now && move !== landed).sort((a, b) => a.at - b.at)
  return { passed, landed, pending: moves.filter((move) => move.at > now) }
}

/**
 * The entries still to sound that name one of these Cells, in Step order: the
 * Steps a paint on those Cells re-cues. The page sounds the Grid it draws, so
 * a Cell that comes to sound differently before its Step does has to be cued
 * again with what it now holds.
 */
export const pendingOn = (
  path: Path,
  reported: Reported | undefined,
  cells: ReadonlyArray<number>
): ReadonlyArray<PathEntry> => pendingEntries(path, reported).filter((entry) => cells.includes(entry.cell))
